import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffectiveTenantId } from './useTenant';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Module keys matching DB columns
export type ModuleKey =
  | 'mod_fichiers'
  | 'mod_magasin'
  | 'mod_gestion'
  | 'mod_stocks'
  | 'mod_fidelite'
  | 'mod_marketing'
  | 'mod_rapports'
  | 'mod_finance'
  | 'mod_equipe'
  | 'mod_website'
  | 'mod_configuration'
  | 'mod_cuisine_kds'
  | 'mod_togo'
  | 'mod_hotel'
  | 'mod_restaurant_salle'
  | 'mod_bar'
  | 'mod_kiosque'
  | 'mod_tablette_serveur'
  | 'mod_caisse_rapide'
  | 'mod_bl'
  | 'mod_magic_sms'
  | 'mod_bons_provisoires'
  | 'mod_ecran_client';

export interface TenantModules {
  id: string;
  tenant_id: string;
  mod_fichiers: boolean;
  mod_magasin: boolean;
  mod_gestion: boolean;
  mod_stocks: boolean;
  mod_fidelite: boolean;
  mod_marketing: boolean;
  mod_rapports: boolean;
  mod_finance: boolean;
  mod_equipe: boolean;
  mod_website: boolean;
  mod_configuration: boolean;
  mod_cuisine_kds: boolean;
  mod_togo: boolean;
  mod_hotel: boolean;
  mod_restaurant_salle: boolean;
  mod_bar: boolean;
  mod_kiosque: boolean;
  mod_tablette_serveur: boolean;
  mod_caisse_rapide: boolean;
  mod_bl: boolean;
  mod_magic_sms: boolean;
  mod_bons_provisoires: boolean;
  mod_ecran_client: boolean;
}

// Labels for display in admin UI
export const MODULE_LABELS: Record<ModuleKey, string> = {
  mod_fichiers: 'Fichiers (Articles, Clients, Fournisseurs)',
  mod_magasin: 'Magasin (Caisse, Etiquettes)',
  mod_gestion: 'Gestion (Pieces, Sessions, Clotures Z, Journal)',
  mod_stocks: 'Stocks (Etat, Mouvements, Inventaires)',
  mod_fidelite: 'Fidélité (Bons, Cartes cadeaux, SMS)',
  mod_marketing: 'Marketing (Promotions, Opérations)',
  mod_rapports: 'Rapports (Ventes, Palmarès)',
  mod_finance: 'Finance (Coffre, Comptabilité)',
  mod_equipe: 'Équipe (Utilisateurs, Profils)',
  mod_website: 'Website (Commandes, Paramètres)',
  mod_configuration: 'Configuration',
  mod_cuisine_kds: 'Cuisine / KDS (Affichage & Impression cuisine)',
  mod_togo: 'Togo (Imprimantes, Tickets, TPE)',
  mod_hotel: 'Hôtel (Chambres, Séjours, Voyageurs, Taxe de séjour)',
  mod_restaurant_salle: 'Restaurant Salle (Plans de salle, Tables, Commandes)',
  mod_bar: 'Bar (Ardoises, Happy Hours, Comptoir)',
  mod_kiosque: 'Kiosque (Commandes autonomes, Bornes)',
  mod_tablette_serveur: 'Tablette Serveur (Prise de commande mobile, multi-tables)',
  mod_caisse_rapide: 'Caisse rapide (écran de vente express)',
  mod_bl: 'Bons de livraison (commande → BL signé → facture)',
  mod_magic_sms: 'Magic SMS (Campagnes IA, rappels, recharge)',
  mod_bons_provisoires:
    'Bons de commande provisoires (ticket W — bon cuisine + addition avant paiement)',
  mod_ecran_client: 'Écran client (playlist médias + miroir panier sur support dédié)',
};

// Default values when no row exists yet
const DEFAULTS: TenantModules = {
  id: '',
  tenant_id: '',
  mod_fichiers: true,
  mod_magasin: true,
  mod_gestion: true,
  mod_stocks: true,
  mod_fidelite: false,
  mod_marketing: false,
  mod_rapports: true,
  mod_finance: false,
  mod_equipe: true,
  mod_website: false,
  mod_configuration: true,
  mod_cuisine_kds: false,
  mod_togo: false,
  mod_hotel: false,
  mod_restaurant_salle: false,
  mod_bar: false,
  mod_kiosque: false,
  mod_tablette_serveur: false,
  mod_caisse_rapide: true,
  mod_bl: false,
  mod_magic_sms: false,
  mod_bons_provisoires: false,
  mod_ecran_client: false,
};

export function useTenantModules() {
  const tenantId = useEffectiveTenantId();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['tenant_modules', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_modules' as any)
        .select('*')
        .eq('tenant_id', tenantId!)
        .maybeSingle();

      if (error) throw error;
      return (data as unknown as TenantModules) || { ...DEFAULTS, tenant_id: tenantId! };
    },
    enabled: !!tenantId,
    // 30s (ex 5 min) : un toggle module admin doit être visible côté caisse
    // au prochain focus/navigation, pas 5 minutes plus tard (bug Wave 2026-07-07).
    staleTime: 30 * 1000,
  });

  return {
    modules: query.data || DEFAULTS,
    isLoading: query.isLoading,
    isEnabled: (key: ModuleKey): boolean => {
      // Magasin = cœur du POS, toujours actif peu importe le toggle DB.
      // Désactiver Magasin rendrait la caisse inaccessible → garde-fou côté lecture.
      if (key === 'mod_magasin') return true;
      return query.data ? ((query.data as any)[key] ?? DEFAULTS[key]) : DEFAULTS[key];
    },
  };
}

// Mutation hook for admin to toggle modules
// Uses admin-users edge function for super_admin (bypasses RLS)
// Uses direct Supabase update for regular admin (passes RLS)
export function useUpdateTenantModules() {
  const tenantId = useEffectiveTenantId();
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const isSuperAdmin = userRole === 'super_admin';

  return useMutation({
    mutationFn: async (updates: Partial<Record<ModuleKey, boolean>>) => {
      if (!tenantId) throw new Error('Tenant non trouvé');

      if (isSuperAdmin) {
        // Super admin: use edge function with service_role to bypass RLS
        const { data, error } = await supabase.functions.invoke('admin-users', {
          body: {
            action: 'update_tenant_modules',
            tenant_id: tenantId,
            modules: updates,
          },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
      } else {
        // Regular admin: direct update (RLS allows own tenant)
        const { error } = await supabase
          .from('tenant_modules' as any)
          .update({ ...updates, updated_at: new Date().toISOString() })
          .eq('tenant_id', tenantId);

        if (error) throw error;
      }
    },
    onSuccess: (_data, updates) => {
      queryClient.invalidateQueries({ queryKey: ['tenant_modules', tenantId] });
      toast.success('Modules mis à jour');

      // NF525 JET: log TOGGLE_MODULE events (fire-and-forget)
      for (const [key, enabled] of Object.entries(updates)) {
        supabase
          .from('fiscal_events')
          .insert({
            event_type: 'TOGGLE_MODULE',
            tenant_id: tenantId,
            details: { module_key: key, enabled },
          } as any)
          .then(
            () => {},
            (e) => console.error('[NF525] Failed to log TOGGLE_MODULE:', e),
          );
      }
    },
    onError: (err: any) => {
      toast.error(`Erreur: ${err.message}`);
    },
  });
}
